import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { eficienciaTelevisaoReducer, initialState } from "./reducer";
import { colors, getResponsiveStyles, styles } from "./styles";

import api from "../../../services/api";

const pollingOptions = [5, 10, 15, 30];
const televisionTokenStorageKey = "eficienciaTelevisaoToken";

const getTelevisionToken = () => {
    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.get("token") || sessionStorage.getItem(televisionTokenStorageKey) || "";
};

const formatPercent = (value) => {
    if (value === null || value === undefined) return "-";

    const numericValue = Number(String(value).replace(",", "."));

    if (!Number.isFinite(numericValue)) return "-";

    return `${numericValue.toFixed(1).replace(".", ",")}%`;
};

const formatMeters = (value) => {
    const numericValue = Number(String(value ?? 0).replace(",", "."));

    if (!Number.isFinite(numericValue)) return "-";

    return numericValue.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
};

const formatCmpx = (value) => {
    if (value === null || value === undefined) return "-";

    const numericValue = Number(String(value).replace(",", "."));

    if (!Number.isFinite(numericValue)) return "-";

    return numericValue.toFixed(1).replace(".", ",");
};

const getEfficiencyColor = (value, efficiencyThreshold) => {
    if (value === null || value === undefined) return colors.danger;

    return value >= efficiencyThreshold ? colors.success : colors.danger;
};

const getEfficiencyTrend = (value, previousValue) => {
    if (value === null || value === undefined || previousValue === null || previousValue === undefined) return null;

    const numericValue = Number(String(value).replace(",", "."));
    const numericPreviousValue = Number(String(previousValue).replace(",", "."));

    if (!Number.isFinite(numericValue) || !Number.isFinite(numericPreviousValue) || numericValue === numericPreviousValue) {
        return null;
    }

    return numericValue > numericPreviousValue ? "up" : "down";
};

const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

function EfficiencyBox({
    label,
    value,
    previousValue,
    tramaCmpx,
    urdumeCmpx,
    showBreaks,
    efficiencyThreshold,
    responsiveStyles,
    large = false
}) {
    const trend = getEfficiencyTrend(value, previousValue);

    return (
        <div style={large
            ? { ...styles.generalBox, ...responsiveStyles.generalBox }
            : { ...styles.groupBox, ...responsiveStyles.groupBox }}
        >
            <div style={large
                ? { ...styles.generalHeader, ...responsiveStyles.generalHeader }
                : { ...styles.groupHeader, ...responsiveStyles.groupHeader }}
            >
                {label}
            </div>
            <div
                style={{
                    ...(large
                        ? { ...styles.generalValue, ...responsiveStyles.generalValue }
                        : { ...styles.groupValue, ...responsiveStyles.groupValue }),
                    backgroundColor: getEfficiencyColor(value, efficiencyThreshold)
                }}
            >
                {formatPercent(value)}
                {trend && (
                    <span
                        aria-label={trend === "up" ? "Eficiência aumentou" : "Eficiência diminuiu"}
                        title={trend === "up" ? "Eficiência aumentou" : "Eficiência diminuiu"}
                        style={large ? styles.generalTrend : styles.groupTrend}
                    >
                        {trend === "up" ? "▲" : "▼"}
                    </span>
                )}
            </div>
            {showBreaks && (
                <div style={large
                    ? { ...styles.generalBreaks, ...responsiveStyles.generalBreaks }
                    : { ...styles.groupBreaks, ...responsiveStyles.groupBreaks }}
                >
                    <span>TRAMA/CMPX = {formatCmpx(tramaCmpx)}</span>
                    <span>URDUME/CMPX = {formatCmpx(urdumeCmpx)}</span>
                </div>
            )}
        </div>
    );
}

export function EficienciaTelevisao() {
    const [state, dispatch] = useReducer(eficienciaTelevisaoReducer, initialState);
    const { data, previousData, isLoading, error, pollingMinutes } = state;
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [filterAboveMinimum, setFilterAboveMinimum] = useState(false);
    const [showBreaks, setShowBreaks] = useState(true);
    const tokenRef = useRef(getTelevisionToken());

    useEffect(() => {
        const updateViewportWidth = () => setViewportWidth(window.innerWidth);

        window.addEventListener("resize", updateViewportWidth);

        return () => window.removeEventListener("resize", updateViewportWidth);
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const token = searchParams.get("token");

        if (!token) return;

        tokenRef.current = token;
        sessionStorage.setItem(televisionTokenStorageKey, token);
        searchParams.delete("token");

        const cleanSearch = searchParams.toString();
        const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ""}${window.location.hash}`;

        window.history.replaceState(null, "", cleanUrl);
    }, []);

    const fetchData = useCallback(() => {
        const token = tokenRef.current;

        api.get("/eficiencia-televisao", {
            params: {
                t: Date.now(),
                acima_eficiencia_minima: filterAboveMinimum ? 1 : 0
            },
            headers: {
                ...(token ? { "X-Eficiencia-Televisao-Token": token } : {}),
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
                Expires: "0"
            }
        }).then((response) => {
            dispatch({
                type: "FETCH_SUCCESS",
                payload: {
                    eficiencia_geral: response.data.eficiencia_geral,
                    trama_cmpx: response.data.trama_cmpx,
                    urdume_cmpx: response.data.urdume_cmpx,
                    eficiencia_baixa: response.data.eficiencia_baixa,
                    eficiencia_minima: response.data.eficiencia_minima,
                    grupos: response.data.grupos || [],
                    producao: response.data.producao || initialState.data.producao,
                    updated_at: response.data.updated_at
                }
            });
        }).catch((requestError) => {
            dispatch({
                type: "FETCH_ERROR",
                payload: requestError.response ? requestError.response.data.message : requestError.message
            });
        });
    }, [filterAboveMinimum]);

    useEffect(() => {
        fetchData();

        const interval = setInterval(fetchData, pollingMinutes * 60 * 1000);

        return () => clearInterval(interval);
    }, [fetchData, pollingMinutes]);

    const groups = useMemo(() => {
        const previousGroups = new Map(
            (previousData?.grupos || []).map((item) => [String(item.grupo), item.eficiencia])
        );

        return data.grupos.map((item) => ({
            group: item.grupo,
            efficiency: item.eficiencia,
            tramaCmpx: item.trama_cmpx,
            urdumeCmpx: item.urdume_cmpx,
            previousEfficiency: previousGroups.get(String(item.grupo))
        }));
    }, [data.grupos, previousData]);

    const halfGroups = Math.ceil(groups.length / 2);
    const firstColumnGroups = groups.slice(0, halfGroups);
    const secondColumnGroups = groups.slice(halfGroups);
    const efficiencyThreshold = data.eficiencia_baixa ?? 88;
    const maxGroupsPerColumn = Math.max(firstColumnGroups.length, secondColumnGroups.length);
    const responsiveStyles = getResponsiveStyles(maxGroupsPerColumn, viewportWidth);
    const productionPercentage = Number(data.producao.percentual);
    const progressWidth = Number.isFinite(productionPercentage)
        ? Math.min(Math.max(productionPercentage, 0), 100)
        : 0;
    const productionEncouragement = productionPercentage >= 100
        ? { message: "META ALCANÇADA!", style: styles.productionProgressReached }
        : productionPercentage >= 90
            ? { message: "FALTA POUCO!", style: styles.productionProgressNear }
            : null;

    if (!isLoading && error) {
        return (
            <main style={{ ...styles.page, ...responsiveStyles.page }}>
                <section style={{ ...styles.errorPanel, ...responsiveStyles.errorPanel }}>
                    <div style={{ ...styles.errorTitle, ...responsiveStyles.errorTitle }}>FALHA AO CARREGAR</div>
                    <div style={{ ...styles.errorMessage, ...responsiveStyles.errorMessage }}>{error}</div>
                </section>
            </main>
        );
    }

    return (
        <main style={{ ...styles.page, ...responsiveStyles.page }}>
            <section style={{ ...styles.panel, ...responsiveStyles.panel }}>
                <header style={{ ...styles.header, ...responsiveStyles.header }}>
                    <h1 style={{ ...styles.title, ...responsiveStyles.title }}>RELATÓRIO DE EFICIÊNCIA - POR GRUPO</h1>
                    <div style={{ ...styles.dateTime, ...responsiveStyles.dateTime }}>
                        DATA HORA:&nbsp;
                        <strong>{formatDateTime(data.updated_at)}</strong>
                    </div>
                </header>

                <div style={{ ...styles.refreshControl, ...responsiveStyles.refreshControl }}>
                    <button
                        type="button"
                        aria-pressed={filterAboveMinimum}
                        onClick={() => setFilterAboveMinimum((currentValue) => !currentValue)}
                        style={{
                            ...styles.averageModeButton,
                            ...(filterAboveMinimum ? styles.averageModeButtonActive : {})
                        }}
                    >
                        {filterAboveMinimum
                            ? `Média acima de ${formatPercent(data.eficiencia_minima)}`
                            : "Média de todos os teares"}
                    </button>
                    <button
                        type="button"
                        aria-pressed={showBreaks}
                        onClick={() => setShowBreaks((currentValue) => !currentValue)}
                        style={{
                            ...styles.averageModeButton,
                            ...(showBreaks ? styles.averageModeButtonActive : {})
                        }}
                    >
                        {showBreaks ? "Ocultar quebras" : "Mostrar quebras"}
                    </button>
                    <label htmlFor="pollingMinutes" style={styles.refreshLabel}>Atualizar a cada</label>
                    <select
                        id="pollingMinutes"
                        value={pollingMinutes}
                        onChange={(event) => dispatch({
                            type: "SET_POLLING_MINUTES",
                            payload: Number(event.target.value)
                        })}
                        style={styles.refreshSelect}
                    >
                        {pollingOptions.map((option) => (
                            <option key={option} value={option}>
                                {option} min
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ ...styles.content, ...responsiveStyles.content }}>
                    <div style={{ ...styles.groupsArea, ...responsiveStyles.groupsArea }}>
                        <div style={{ ...styles.groupsColumn, ...responsiveStyles.groupsColumn }}>
                            {firstColumnGroups.map((item) => (
                                <EfficiencyBox
                                    key={item.group}
                                    label={`GRUPO ${item.group}`}
                                    value={item.efficiency}
                                    previousValue={item.previousEfficiency}
                                    tramaCmpx={item.tramaCmpx}
                                    urdumeCmpx={item.urdumeCmpx}
                                    showBreaks={showBreaks}
                                    efficiencyThreshold={efficiencyThreshold}
                                    responsiveStyles={responsiveStyles}
                                />
                            ))}
                        </div>
                        <div style={{ ...styles.groupsColumn, ...responsiveStyles.groupsColumn }}>
                            {secondColumnGroups.map((item) => (
                                <EfficiencyBox
                                    key={item.group}
                                    label={`GRUPO ${item.group}`}
                                    value={item.efficiency}
                                    previousValue={item.previousEfficiency}
                                    tramaCmpx={item.tramaCmpx}
                                    urdumeCmpx={item.urdumeCmpx}
                                    showBreaks={showBreaks}
                                    efficiencyThreshold={efficiencyThreshold}
                                    responsiveStyles={responsiveStyles}
                                />
                            ))}
                        </div>
                    </div>

                    <EfficiencyBox
                        label="GERAL"
                        value={data.eficiencia_geral}
                        previousValue={previousData?.eficiencia_geral}
                        tramaCmpx={data.trama_cmpx}
                        urdumeCmpx={data.urdume_cmpx}
                        showBreaks={showBreaks}
                        efficiencyThreshold={efficiencyThreshold}
                        responsiveStyles={responsiveStyles}
                        large
                    />
                </div>

                <section style={{ ...styles.productionPanel, ...responsiveStyles.productionPanel }}>
                    <div style={{ ...styles.productionHeader, ...responsiveStyles.productionHeader }}>
                        <div style={styles.productionMetric}>
                            <span style={styles.productionLabel}>TURMA</span>
                            <strong style={styles.productionValue}>{data.producao.turma || "-"}</strong>
                        </div>
                        <div style={styles.productionMetric}>
                            <span style={styles.productionLabel}>PRODUÇÃO</span>
                            <strong style={styles.productionValue}>{formatMeters(data.producao.producao_atual)} m</strong>
                        </div>
                        <div style={styles.productionMetric}>
                            <span style={styles.productionLabel}>META DIÁRIA</span>
                            <strong style={styles.productionValue}>{formatMeters(data.producao.meta_producao_diaria)} m</strong>
                        </div>
                        <div style={styles.productionMetric}>
                            <span style={styles.productionLabel}>PROGRESSO</span>
                            <strong style={styles.productionValue}>{formatPercent(data.producao.percentual)}</strong>
                        </div>
                    </div>
                    <div
                        aria-label="Progresso da meta de produção diária"
                        aria-valuemax="100"
                        aria-valuemin="0"
                        aria-valuenow={progressWidth}
                        role="progressbar"
                        style={{ ...styles.productionTrack, ...responsiveStyles.productionTrack }}
                    >
                        <div
                            style={{
                                ...styles.productionProgress,
                                ...productionEncouragement?.style,
                                width: `${progressWidth}%`
                            }}
                        >
                            {productionEncouragement?.message}
                        </div>
                    </div>
                </section>

                {isLoading && <div style={{ ...styles.status, ...responsiveStyles.status }}>Carregando...</div>}
            </section>
        </main>
    );
}
